import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { FieldPath, FieldValues, useController } from 'react-hook-form'
import { LexicalComposer } from '@lexical/react/LexicalComposer'
import { PlainTextPlugin } from '@lexical/react/LexicalPlainTextPlugin'
import { ContentEditable } from '@lexical/react/LexicalContentEditable'
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin'
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary'
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'
import {
    $createParagraphNode,
    $createTextNode,
    $getRoot,
    $getSelection,
    $isRangeSelection,
    $isElementNode,
    EditorState,
    LexicalEditor,
    TextNode,
} from 'lexical'
import { VariableNode, $createVariableNode } from './VariableNode'
import { HelperNode, $createHelperNode } from './HelperNode'
import { isVariablePath, pathToLabel, isHelperExpression, parseHelperExpression } from './variableUtils'
import { helperRegistry, categoryLabels, type HelperDefinition } from './helperRegistry'
import { HelperEditorContext } from './HelperEditorContext'
import { FunctionIcon } from '../icons'
import Button from '../Button'
import { FieldProps, RulePath } from '../../types'
import { snakeToTitle } from '../../utils'
import { CampaignContext, ProjectContext } from '../../contexts'
import { useResolver } from '../../hooks'
import api from '../../api'
import './VariableTextInput.css'

interface Suggestion {
    path: string
    label: string
}

interface AutocompleteSuggestion {
    type: 'variable' | 'helper'
    path: string
    label: string
    description?: string
    category?: string
    helperDef?: HelperDefinition
}

interface VariableTextInputProps {
    name: string
    label?: string
    subtitle?: string
    required?: boolean
    disabled?: boolean
    hideLabel?: boolean
    placeholder?: string
    value?: string
    onChange?: (value: string) => void
    suggestions?: RulePath[]
    multiline?: boolean
}

const serializeToText = (editorState: EditorState): string => {
    let text = ''
    editorState.read(() => {
        const root = $getRoot()
        const paragraphs = root.getChildren()
        text = paragraphs
            .map(paragraph => {
                if ($isElementNode(paragraph)) {
                    return paragraph.getChildren().map(node => {
                        return node.getTextContent()
                    }).join('')
                }
                return paragraph.getTextContent()
            })
            .join('\n')
    })
    return text
}

const deserializeFromText = (text: string, editor: LexicalEditor) => {
    editor.update(() => {
        const root = $getRoot()
        root.clear()

        const lines = text.split('\n')
        for (let i = 0; i < lines.length; i++) {
            const paragraph = $createParagraphNode()
            root.append(paragraph)

            const parts = lines[i].split(/(\{\{\{[^}]+\}\}\}|\{\{[^}]+\}\})/)
            for (const part of parts) {
                if (!part) continue
                const tripleMatch = part.match(/^\{\{\{(.+?)\}\}\}$/)
                const doubleMatch = !tripleMatch ? part.match(/^\{\{(.+?)\}\}$/) : null
                const match = tripleMatch ?? doubleMatch
                if (match) {
                    const inner = match[1].trim()
                    if (isVariablePath(inner)) {
                        paragraph.append($createVariableNode(inner, undefined, !!tripleMatch))
                    } else if (isHelperExpression(inner)) {
                        const parsed = parseHelperExpression(inner)
                        if (parsed) {
                            paragraph.append($createHelperNode(parsed.name, parsed.args))
                        } else {
                            paragraph.append($createTextNode(part))
                        }
                    } else {
                        paragraph.append($createTextNode(part))
                    }
                } else {
                    paragraph.append($createTextNode(part))
                }
            }
        }
    })
}

const helperSuggestions: AutocompleteSuggestion[] = helperRegistry.map(h => ({
    type: 'helper' as const,
    path: h.name,
    label: h.label,
    description: h.description,
    category: categoryLabels[h.category],
    helperDef: h,
}))

const VariableTriggerPlugin = ({ suggestions, onInsertHelper }: {
    suggestions: Suggestion[]
    onInsertHelper: (nodeKey: string) => void
}) => {
    const [editor] = useLexicalComposerContext()
    const [query, setQuery] = useState<string | null>(null)
    const isTripleRef = useRef(false)
    const [position, setPosition] = useState<{ top: number, left: number } | null>(null)
    const [selectedIndex, setSelectedIndex] = useState(0)
    const selectedIndexRef = useRef(0)
    const filteredRef = useRef<AutocompleteSuggestion[]>([])
    const dropdownRef = useRef<HTMLDivElement>(null)

    const filtered = useMemo<AutocompleteSuggestion[]>(() => {
        if (query === null) return []
        const q = query.toLowerCase()

        const variableMatches: AutocompleteSuggestion[] = suggestions
            .filter(s =>
                s.path.toLowerCase().includes(q)
                || s.label.toLowerCase().includes(q),
            )
            .slice(0, 15)
            .map(s => ({ type: 'variable' as const, path: s.path, label: s.label }))

        const helperMatches = helperSuggestions
            .filter(s =>
                s.path.toLowerCase().includes(q)
                || s.label.toLowerCase().includes(q)
                || (s.description?.toLowerCase().includes(q) ?? false),
            )
            .slice(0, 10)

        return [...variableMatches, ...helperMatches]
    }, [query, suggestions])

    filteredRef.current = filtered

    useEffect(() => {
        setSelectedIndex(0)
        selectedIndexRef.current = 0
    }, [filtered.length, query])

    useEffect(() => {
        selectedIndexRef.current = selectedIndex
    }, [selectedIndex])

    useEffect(() => {
        return editor.registerTextContentListener(() => {
            editor.getEditorState().read(() => {
                const selection = $getSelection()
                if (!$isRangeSelection(selection)) {
                    setQuery(null)
                    return
                }

                const anchor = selection.anchor
                const node = anchor.getNode()
                if (!(node instanceof TextNode)) {
                    setQuery(null)
                    return
                }

                const textContent = node.getTextContent()
                const offset = anchor.offset

                const textBefore = textContent.slice(0, offset)
                const tripleIndex = textBefore.lastIndexOf('{{{')
                const doubleIndex = textBefore.lastIndexOf('{{')

                if (doubleIndex === -1) {
                    setQuery(null)
                    return
                }

                const triple = tripleIndex !== -1 && tripleIndex === doubleIndex - 1
                const triggerLen = triple ? 3 : 2
                const triggerIndex = triple ? tripleIndex : doubleIndex
                const textAfterTrigger = textBefore.slice(triggerIndex + triggerLen)
                const closingBrackets = triple ? '}}}' : '}}'
                if (textAfterTrigger.includes(closingBrackets)) {
                    setQuery(null)
                    return
                }

                isTripleRef.current = triple
                setQuery(textAfterTrigger)

                const domSelection = window.getSelection()
                if (domSelection && domSelection.rangeCount > 0) {
                    const range = domSelection.getRangeAt(0)
                    const rect = range.getBoundingClientRect()
                    setPosition({
                        top: rect.bottom + window.scrollY + 4,
                        left: rect.left + window.scrollX,
                    })
                }
            })
        })
    }, [editor])

    const insertVariable = useCallback((path: string) => {
        const raw = isTripleRef.current
        editor.update(() => {
            const selection = $getSelection()
            if (!$isRangeSelection(selection)) return

            const anchor = selection.anchor
            const node = anchor.getNode()
            if (!(node instanceof TextNode)) return

            const textContent = node.getTextContent()
            const offset = anchor.offset
            const textBefore = textContent.slice(0, offset)
            const triggerIndex = raw
                ? textBefore.lastIndexOf('{{{')
                : textBefore.lastIndexOf('{{')

            if (triggerIndex === -1) return

            const textBeforeTrigger = textContent.slice(0, triggerIndex)
            const textAfterCursor = textContent.slice(offset)

            const variableNode = $createVariableNode(path, undefined, raw)

            if (textBeforeTrigger) {
                const beforeNode = $createTextNode(textBeforeTrigger)
                node.replace(beforeNode)
                beforeNode.insertAfter(variableNode)
            } else {
                node.replace(variableNode)
            }

            if (textAfterCursor) {
                const afterNode = $createTextNode(textAfterCursor)
                variableNode.insertAfter(afterNode)
                afterNode.select(0, 0)
            } else {
                const spaceNode = $createTextNode(' ')
                variableNode.insertAfter(spaceNode)
                spaceNode.select(1, 1)
            }
        })

        setQuery(null)
    }, [editor])

    const insertHelper = useCallback((helperDef: HelperDefinition) => {
        editor.update(() => {
            const selection = $getSelection()
            if (!$isRangeSelection(selection)) return

            const anchor = selection.anchor
            const node = anchor.getNode()
            if (!(node instanceof TextNode)) return

            const textContent = node.getTextContent()
            const offset = anchor.offset
            const textBefore = textContent.slice(0, offset)
            const triggerIndex = textBefore.lastIndexOf('{{')

            if (triggerIndex === -1) return

            const textBeforeTrigger = textContent.slice(0, triggerIndex)
            const textAfterCursor = textContent.slice(offset)

            const defaultArgs = helperDef.params.map(p => ({
                value: p.default != null ? String(p.default) : '',
                quoted: p.type !== 'variable',
            }))
            const helperNode = $createHelperNode(helperDef.name, defaultArgs)

            if (textBeforeTrigger) {
                const beforeNode = $createTextNode(textBeforeTrigger)
                node.replace(beforeNode)
                beforeNode.insertAfter(helperNode)
            } else {
                node.replace(helperNode)
            }

            if (textAfterCursor) {
                const afterNode = $createTextNode(textAfterCursor)
                helperNode.insertAfter(afterNode)
                afterNode.select(0, 0)
            } else {
                const spaceNode = $createTextNode(' ')
                helperNode.insertAfter(spaceNode)
                spaceNode.select(1, 1)
            }

            onInsertHelper(helperNode.getKey())
        })

        setQuery(null)
    }, [editor, onInsertHelper])

    const handleSelect = useCallback((item: AutocompleteSuggestion) => {
        if (item.type === 'helper' && item.helperDef) {
            insertHelper(item.helperDef)
        } else {
            insertVariable(item.path)
        }
    }, [insertVariable, insertHelper])

    useEffect(() => {
        if (query === null || filtered.length === 0) return

        const handleKeyDown = (event: KeyboardEvent) => {
            const items = filteredRef.current
            if (items.length === 0) return

            if (event.key === 'ArrowDown') {
                event.preventDefault()
                setSelectedIndex(i => {
                    const next = (i + 1) % items.length
                    selectedIndexRef.current = next
                    return next
                })
            } else if (event.key === 'ArrowUp') {
                event.preventDefault()
                setSelectedIndex(i => {
                    const next = (i - 1 + items.length) % items.length
                    selectedIndexRef.current = next
                    return next
                })
            } else if (event.key === 'Enter' || event.key === 'Tab') {
                event.preventDefault()
                handleSelect(items[selectedIndexRef.current])
            } else if (event.key === 'Escape') {
                event.preventDefault()
                setQuery(null)
            }
        }

        document.addEventListener('keydown', handleKeyDown, true)
        return () => document.removeEventListener('keydown', handleKeyDown, true)
    }, [query, filtered.length, handleSelect])

    if (query === null || filtered.length === 0 || !position) return null

    const variableItems = filtered.filter(s => s.type === 'variable')
    const helperItems = filtered.filter(s => s.type === 'helper')
    let globalIndex = 0

    return (
        <div
            ref={dropdownRef}
            className="variable-dropdown"
            style={{ top: position.top, left: position.left }}
        >
            {variableItems.length > 0 && helperItems.length > 0 && (
                <div className="variable-dropdown-section">Data</div>
            )}
            {variableItems.map(suggestion => {
                const idx = globalIndex++
                return (
                    <div
                        key={suggestion.path}
                        ref={idx === selectedIndex ? el => el?.scrollIntoView({ block: 'nearest' }) : undefined}
                        className={`variable-dropdown-item${idx === selectedIndex ? ' selected' : ''}`}
                        onMouseDown={(e) => {
                            e.preventDefault()
                            handleSelect(suggestion)
                        }}
                        onMouseEnter={() => setSelectedIndex(idx)}
                    >
                        <span className="variable-dropdown-label">{suggestion.label}</span>
                        <span className="variable-dropdown-path">{suggestion.path}</span>
                    </div>
                )
            })}
            {helperItems.length > 0 && variableItems.length > 0 && (
                <div className="variable-dropdown-section">Functions</div>
            )}
            {helperItems.map(suggestion => {
                const idx = globalIndex++
                return (
                    <div
                        key={suggestion.path}
                        ref={idx === selectedIndex ? el => el?.scrollIntoView({ block: 'nearest' }) : undefined}
                        className={`variable-dropdown-item helper${idx === selectedIndex ? ' selected' : ''}`}
                        onMouseDown={(e) => {
                            e.preventDefault()
                            handleSelect(suggestion)
                        }}
                        onMouseEnter={() => setSelectedIndex(idx)}
                    >
                        <div className="variable-dropdown-label-row">
                            <FunctionIcon />
                            <span className="variable-dropdown-label">{suggestion.label}</span>
                            {suggestion.category && (
                                <span className="variable-dropdown-category">{suggestion.category}</span>
                            )}
                        </div>
                        {suggestion.description && (
                            <span className="variable-dropdown-path">{suggestion.description}</span>
                        )}
                    </div>
                )
            })}
        </div>
    )
}

// Exposes an insert-trigger function to open the autocomplete dropdown
const InsertTriggerPlugin = ({ triggerRef }: { triggerRef: React.MutableRefObject<(() => void) | null> }) => {
    const [editor] = useLexicalComposerContext()

    useEffect(() => {
        triggerRef.current = () => {
            editor.update(() => {
                const selection = $getSelection()
                if ($isRangeSelection(selection)) {
                    selection.insertRawText('{{')
                } else {
                    const root = $getRoot()
                    const lastChild = root.getLastChild()
                    if ($isElementNode(lastChild)) {
                        lastChild.selectEnd()
                    } else {
                        root.selectEnd()
                    }
                    const newSelection = $getSelection()
                    if ($isRangeSelection(newSelection)) {
                        newSelection.insertRawText('{{')
                    }
                }
            })
            editor.focus()
        }
        return () => { triggerRef.current = null }
    }, [editor, triggerRef])

    return null
}

// Handles both initial deserialization and external value sync
const ValueSyncPlugin = ({ value }: { value: string }) => {
    const [editor] = useLexicalComposerContext()
    const initialized = useRef(false)
    const lastExternalValue = useRef(value)
    const lastInternalValue = useRef(value)

    useEffect(() => {
        if (!initialized.current) {
            initialized.current = true
            if (value) {
                deserializeFromText(value, editor)
            }
            return
        }
        if (value !== lastExternalValue.current && value !== lastInternalValue.current) {
            lastExternalValue.current = value
            deserializeFromText(value, editor)
        }
    }, [value, editor])

    useEffect(() => {
        return editor.registerUpdateListener(({ editorState }) => {
            lastInternalValue.current = serializeToText(editorState)
            lastExternalValue.current = lastInternalValue.current
        })
    }, [editor])

    return null
}

const useSuggestions = (skip: boolean): RulePath[] => {
    const [project] = useContext(ProjectContext)
    const [campaign] = useContext(CampaignContext)
    const campaignId = campaign?.id && campaign?.type === 'trigger' ? campaign.id : undefined

    const [suggestions] = useResolver(
        useCallback(
            async () => skip ? null : await api.projects.pathSuggestions(project.id, campaignId),
            [project.id, campaignId, skip],
        ),
    )

    return useMemo(() => [
        ...(suggestions?.userPaths ?? []),
        ...(suggestions?.journeyPaths ?? []),
    ], [suggestions])
}

export default function VariableTextInput({
    name,
    label,
    subtitle,
    required,
    disabled,
    hideLabel = false,
    placeholder,
    value = '',
    onChange,
    suggestions: externalSuggestions,
    multiline = false,
}: VariableTextInputProps) {

    const insertTriggerRef = useRef<(() => void) | null>(null)
    const [autoOpenNodeKey, setAutoOpenNodeKey] = useState<string | null>(null)
    const fetchedSuggestions = useSuggestions(!!externalSuggestions)
    const suggestions = externalSuggestions ?? fetchedSuggestions

    const mappedSuggestions = useMemo<Suggestion[]>(() =>
        suggestions.map(s => {
            const field = s.path.startsWith('$.') ? s.path.substring(2) : s.path
            const path = `${s.type}.${field}`
            return { path, label: pathToLabel(path) }
        }),
    [suggestions])

    const initialConfig = useMemo(() => ({
        namespace: `variable-input-${name}`,
        nodes: [VariableNode, HelperNode],
        onError: (error: Error) => {
            console.error('Lexical error:', error)
        },
        editable: !disabled,
    }), [name, disabled])

    const handleChange = useCallback((editorState: EditorState) => {
        const text = serializeToText(editorState)
        onChange?.(text)
    }, [onChange])

    const handleInsertHelper = useCallback((nodeKey: string) => {
        setAutoOpenNodeKey(nodeKey)
    }, [])

    const clearAutoOpen = useCallback(() => {
        setAutoOpenNodeKey(null)
    }, [])

    const helperContextValue = useMemo(() => ({
        suggestions: mappedSuggestions,
        autoOpenNodeKey,
        clearAutoOpen,
    }), [mappedSuggestions, autoOpenNodeKey, clearAutoOpen])

    const labelId = `variable-input-label-${name}`

    return (
        <div className={`ui-text-input variable-text-input${hideLabel ? ' hide-label' : ''}${disabled ? ' disabled' : ''}`}>
            {!hideLabel && (
                <label id={labelId}>
                    {label ?? snakeToTitle(name)}
                    {required && <span className="input-required" style={{ color: 'red' }}>&nbsp;*</span>}
                </label>
            )}
            {subtitle && <span className="label-subtitle">{subtitle}</span>}
            <LexicalComposer initialConfig={initialConfig}>
                <HelperEditorContext.Provider value={helperContextValue}>
                    <div className={`variable-editor-wrapper${multiline ? ' multiline' : ''}`}>
                        <PlainTextPlugin
                            contentEditable={
                                <ContentEditable
                                    className={`variable-editor-content${multiline ? ' multiline' : ''}`}
                                    ariaLabelledBy={hideLabel ? undefined : labelId}
                                />
                            }
                            placeholder={placeholder
                                ? <span className="variable-editor-placeholder">{placeholder}</span>
                                : null}
                            ErrorBoundary={LexicalErrorBoundary}
                        />
                        <OnChangePlugin onChange={handleChange} />
                        <ValueSyncPlugin value={value} />
                        <InsertTriggerPlugin triggerRef={insertTriggerRef} />
                        <VariableTriggerPlugin
                            suggestions={mappedSuggestions}
                            onInsertHelper={handleInsertHelper}
                        />
                        <Button
                            size="tiny"
                            variant="secondary"
                            className={`variable-insert-button${multiline ? ' multiline' : ''}`}
                            title="Insert Variable or Helper"
                            tabIndex={-1}
                            onMouseDown={(e) => {
                                e.preventDefault()
                                insertTriggerRef.current?.()
                            }}
                        >{'{\u00B7}'}</Button>
                    </div>
                </HelperEditorContext.Provider>
            </LexicalComposer>
        </div>
    )
}

VariableTextInput.Field = function VariableTextInputField<X extends FieldValues, P extends FieldPath<X>>({
    form,
    name,
    required,
    suggestions,
    ...rest
}: Omit<VariableTextInputProps, 'value' | 'onChange'> & FieldProps<X, P>) {
    const { field } = useController({
        control: form.control,
        name,
        rules: { required },
    })

    return (
        <VariableTextInput
            {...rest}
            name={name}
            required={required}
            value={field.value ?? ''}
            onChange={field.onChange}
            suggestions={suggestions}
        />
    )
}
